import { useState } from 'react';
import { POS_PERMISSIONS } from '@matix/types';
import { useCanI } from '../../lib/permissions';
import { useModuleOn } from '../../store/tenantConfig';
import { useLang } from '../../lib/language';
import { QuickSalesReport } from './QuickSalesReport';
import { BusinessSummaryReport } from './BusinessSummaryReport';
import { SalesBySalesmanReport } from './SalesBySalesmanReport';
import { ItemDetailsReport } from './ItemDetailsReport';
import { MyShiftsReport } from './MyShiftsReport';
import { ItemsStockReport } from './ItemsStockReport';
import { ReturnsByItemReport, WasteByItemReport } from './ReturnsWasteReports';
import { TransfersReport } from './TransfersReport';

const REPORTS: { key: string; label: string; perm: string | null; Component: () => JSX.Element }[] = [
  { key: 'quick-sales', label: 'Quick Sales', perm: POS_PERMISSIONS.ViewQuickSales, Component: QuickSalesReport },
  { key: 'business-summary', label: 'Business Summary', perm: POS_PERMISSIONS.ViewBusinessSummary, Component: BusinessSummaryReport },
  { key: 'sales-by-salesman', label: 'Sales by Salesman', perm: POS_PERMISSIONS.ViewSalesBySalesman, Component: SalesBySalesmanReport },
  { key: 'item-details', label: 'Sales by Items', perm: POS_PERMISSIONS.ViewItemDetails, Component: ItemDetailsReport },
  // Same gate as the topbar's Stock Balance icon (the legacy name) — see
  // REPORT_PERMISSIONS.itemsStock in pos-api.
  { key: 'items-stock', label: 'Items Stock', perm: POS_PERMISSIONS.ViewSalesBySalesman, Component: ItemsStockReport },
  // The cashier's OWN shifts — their own data, so no report permission gates it.
  { key: 'my-shifts', label: 'My Shifts', perm: null, Component: MyShiftsReport },
  // What came back over this branch's counter, and what was written off —
  // two reports, each summed per item. Open to every cashier, like My Shifts;
  // the server pins both to the JWT's branch.
  { key: 'returns-by-item', label: 'Returns', perm: null, Component: ReturnsByItemReport },
  { key: 'waste-by-item', label: 'Waste', perm: null, Component: WasteByItemReport },
  // Store transfers in and out of this branch's store. Gated on the same legacy
  // "Create Transfer Transaction" row as the Transfers workspace, so a cashier
  // who may not move stock between stores does not read the movements either.
  { key: 'transfers', label: 'Transfers', perm: POS_PERMISSIONS.CreateTransferTransaction, Component: TransfersReport },
];

const REPORT_LABELS: Record<string, { en: string; ar: string }> = {
  'quick-sales': { en: 'Quick Sales', ar: 'ملخص المبيعات' },
  'business-summary': { en: 'Business Summary', ar: 'ملخص الأعمال' },
  'sales-by-salesman': { en: 'Sales by Salesman', ar: 'المبيعات حسب المندوب' },
  'item-details': { en: 'Sales by Items', ar: 'تقرير المبيعات بالاصناف' },
  'items-stock': { en: 'Items Stock', ar: 'مخزون الاصناف' },
  'my-shifts': { en: 'My Shifts', ar: 'ورديّاتي' },
  'returns-by-item': { en: 'Returns', ar: 'تقرير المرتجعات' },
  'waste-by-item': { en: 'Waste', ar: 'تقرير التوالف' },
  transfers: { en: 'Transfers', ar: 'التحويلات المخزنية' },
};

/**
 * Reports workspace. The visible reports are derived from the logged-in
 * user's permission names in the JWT, so the list never depends on a separate
 * backend round-trip. "My Shifts" is always present — it is the cashier's own
 * data — which is why the Sidebar shows the Reports entry to everyone. The
 * backend still re-checks each gated report's permission on its data endpoint
 * (defense in depth).
 */
export function ReportsScreen(): JSX.Element {
  const { t } = useLang();
  // useCanI must be called unconditionally (hook rules), so evaluate each.
  const canQuick = useCanI(POS_PERMISSIONS.ViewQuickSales);
  const canBiz = useCanI(POS_PERMISSIONS.ViewBusinessSummary);
  const canSalesman = useCanI(POS_PERMISSIONS.ViewSalesBySalesman);
  const canItems = useCanI(POS_PERMISSIONS.ViewItemDetails);
  const canTransfers = useCanI(POS_PERMISSIONS.CreateTransferTransaction);
  // Business-profile modules (setup wizard): stock reports need Inventory,
  // the per-salesperson report needs Sales staff. All on for every tenant
  // onboarded before the wizard.
  const inventoryOn = useModuleOn('inventory');
  const salesStaffOn = useModuleOn('salesStaff');
  const allow: Record<string, boolean> = {
    'quick-sales': canQuick,
    'business-summary': canBiz,
    'sales-by-salesman': canSalesman && salesStaffOn,
    'item-details': canItems,
    'items-stock': canSalesman && inventoryOn,
    'my-shifts': true,
    'returns-by-item': true,
    'waste-by-item': true,
    transfers: canTransfers && inventoryOn,
  };
  const available = REPORTS.filter((r) => allow[r.key]);

  const [active, setActive] = useState<string | null>(null);
  const current = available.find((r) => r.key === active) ?? available[0];

  return (
    <div className="reports-screen">
      <div className="reports-header">
        <h1>{t({ en: 'Reports', ar: 'التقارير' })}</h1>
      </div>

      {available.length === 0 && (
        <div className="rpt-empty">{t({ en: 'You don’t have permission to view any reports.', ar: 'ليس لديك صلاحية لعرض أي تقارير.' })}</div>
      )}

      {available.length > 0 && (
        <>
          <div className="reports-tabs">
            {available.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`reports-tab ${current?.key === r.key ? 'active' : ''}`}
                onClick={() => setActive(r.key)}
              >
                {t(REPORT_LABELS[r.key] ?? { en: r.label, ar: r.label })}
              </button>
            ))}
          </div>
          {current && <current.Component />}
        </>
      )}
    </div>
  );
}
